import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/lib/auth';
import { LOGIN_ENABLED } from '@/lib/features';

// ─── Shared loading spinner ───────────────────────────────────────────────────
function AuthSpinner() {
  return (
    <div className="min-h-screen bg-black flex items-center justify-center">
      <div className="w-8 h-8 border-2 border-yellow-400 border-t-transparent
                      rounded-full animate-spin" />
    </div>
  );
}

// ─── ProtectedRoute ───────────────────────────────────────────────────────────
/**
 * Wraps a route that requires the user to be logged in.
 *
 * Behaviour:
 *  - While session is loading  → spinner (avoids flash-redirect on page refresh)
 *  - Guest user                → redirect to /login, preserving the intended URL
 *  - Authenticated user        → renders children
 */
export function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();
  const location = useLocation();

  // Login is switched off: nobody can be signed in, so send them home.
  if (!LOGIN_ENABLED) return <Navigate to="/" replace />;

  if (isLoading) return <AuthSpinner />;

  if (!isAuthenticated) {
    // Pass the attempted path so LoginPage can redirect back after sign-in
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  return <>{children}</>;
}

// ─── AdminRoute ───────────────────────────────────────────────────────────────
/**
 * Wraps a route that requires role === 'admin'.
 *
 * Behaviour:
 *  - While loading             → spinner
 *  - Guest user                → redirect to /login
 *  - Logged-in non-admin       → redirect to /unauthorized (not silently to home)
 *  - Admin user                → renders children
 */
export function AdminRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isStaff, isLoading } = useAuth();
  const location = useLocation();

  if (!LOGIN_ENABLED) return <Navigate to="/" replace />;

  if (isLoading) return <AuthSpinner />;

  if (!isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  // Admins (Level-1) and instructors (Level-2) both reach the admin area;
  // the shell + per-procedure checks scope what each can see/do.
  if (!isStaff) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
}
